/**
 * SouthIndianTradition — content model + resolver.
 *
 * Data-driven template for a traditional South Indian wedding
 * (Tamil/Telugu/Kannada style). Manages both pre-wedding and main ceremony
 * sections with proper cultural authenticity and timing.
 */

import type { InvitationData } from '../types';

function img(id: string, w = 1400): string {
  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;
}

const STOCK = {
  hero: img('1601297561143-796741160e8a', 2000),
  temple: img('1505751571885-8613e7dcf516', 900),
  couple: img('1606216794074-735e91aa2c92', 900),
  garland: img('1532712938310-34cb3982ef74', 900),
  dinner: img('1555939594-58d7cb561ad1', 900),
};

export interface Event {
  title: string;
  date: string;
  time: string;
  venue: string;
  description: string;
  image?: string;
}

export interface SouthIndianContent {
  hero: {
    kicker: string;
    title: string;
    subtitle: string;
    host: string;
    dateLabel: string;
    venueShort: string;
    background: string;
  };
  about: {
    label: string;
    title: string;
    description: string;
    image: string;
  };
  ceremony: {
    label: string;
    title: string;
    events: Event[];
  };
  rsvp: {
    label: string;
    title: string;
    description: string;
    deadline: string;
    externalLink?: string;
  };
  footer: {
    message: string;
    signature: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────

const hasText = (v?: string | null): v is string =>
  typeof v === 'string' && v.trim().length > 0;

function formatLong(iso?: string): string {
  if (!hasText(iso)) return 'Date to be announced';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function formatTime(iso?: string, fallback = ''): string {
  if (!hasText(iso)) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// ── Demo Content ───────────────────────────────────────────────────────────

const DEMO: SouthIndianContent = {
  hero: {
    kicker: 'A sacred ceremony in the South Indian tradition',
    title: 'Lakshmi & Ram',
    subtitle: 'joining their lives in the temple of love',
    host: 'The Reddy & Nair Families',
    dateLabel: 'Saturday, 2 March 2025',
    venueShort: 'Sundararajan Temple, Chennai',
    background: STOCK.hero,
  },
  about: {
    label: 'About',
    title: 'An auspicious South Indian marriage ceremony',
    description:
      'Join us for a traditional South Indian ceremony combining ancient rituals with modern celebrations. The ceremony will include the Mangala Sutra ceremony, Kanyadaan, and a traditional reception with authentic Tamil/Telugu/Kannada cultural elements.',
    image: STOCK.temple,
  },
  ceremony: {
    label: 'Ceremony Details',
    title: 'A sacred day of blessings',
    events: [
      {
        title: 'Nandi Pilgrimage',
        date: 'Saturday, 1 March 2025',
        time: '7:00 AM',
        venue: 'Tiruvannamalai Temple',
        description: 'Traditional pilgrimage by both families before the wedding',
        image: STOCK.garland,
      },
      {
        title: 'Mangala Sutra',
        date: 'Sunday, 2 March 2025',
        time: '10:00 AM',
        venue: 'Sundararajan Temple, Chennai',
        description: 'The sacred thread ceremony symbolizing marital bond',
        image: STOCK.temple,
      },
      {
        title: 'Wedding Reception',
        date: 'Sunday, 2 March 2025',
        time: '6:00 PM',
        venue: 'Grand Palace, Chennai',
        description: 'Traditional dinner with cultural performances and dance',
        image: STOCK.dinner,
      },
    ],
  },
  rsvp: {
    label: 'Join Us',
    title: 'Kindly Respond',
    description:
      'Your blessings and presence make our ceremony complete. Please respond by the date below.',
    deadline: '15 February 2025',
  },
  footer: {
    message: 'With prayers and traditional blessings',
    signature: 'Lakshmi & Ram',
  },
};

// ── Resolver ──────────────────────────────────────────────────────────────

export function resolveContent(data: InvitationData): SouthIndianContent {
  const wedding = data.customData?.wedding ?? {};
  const [nameA, nameB] = (data.eventName || '').split(/\s*(?:&|and)\s*/i).filter(Boolean);
  const coupleNames = nameA && nameB ? `${nameA} & ${nameB}` : DEMO.hero.title;

  const scheduleEvents: Event[] | undefined = wedding.weddingEvents?.length
    ? wedding.weddingEvents.map((e: any, i: number) => ({
        title: e.title,
        date: formatLong(e.date),
        time: [formatTime(e.startTime), formatTime(e.endTime)].filter(Boolean).join(' – '),
        venue: e.venue,
        description: e.description || '',
        image: e.coverImageUrl ?? STOCK.temple,
      }))
    : undefined;

  return {
    hero: {
      kicker: DEMO.hero.kicker,
      title: coupleNames,
      subtitle: DEMO.hero.subtitle,
      host: hasText(data.hostName) ? data.hostName : DEMO.hero.host,
      dateLabel: hasText(data.eventDate) ? formatLong(data.eventDate) : DEMO.hero.dateLabel,
      venueShort: hasText(data.eventVenue) ? data.eventVenue : DEMO.hero.venueShort,
      background: data.mediaUrls?.[0] ?? DEMO.hero.background,
    },
    about: {
      ...DEMO.about,
    },
    ceremony: {
      ...DEMO.ceremony,
      events: scheduleEvents ?? DEMO.ceremony.events,
    },
    rsvp: {
      ...DEMO.rsvp,
      description: hasText(data.customMessage) ? data.customMessage : DEMO.rsvp.description,
      externalLink: hasText(data.rsvpLink) ? data.rsvpLink : undefined,
    },
    footer: {
      message: DEMO.footer.message,
      signature: coupleNames,
    },
  };
}
